import React from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { Assessment } from '../../state/reduxRepositories/assessments/assessments.interfaces';
import { dateTime } from '../../time/datetime';
import { useCustomTranslation } from '../../translation/customTranslation';

interface SymptomAsessmentDataDetail {
  answer: number;
  title: { l10nKey: string; l10nValue: string };
  description: { l10nKey: string; l10nValue: string };
}

interface SymptomAsessmentData {
  data: SymptomAsessmentDataDetail[];
  score: number;
  totalSteps: number;
}

interface SymptomAsessmentDetailProps {
  onClose: () => void;
  symptomAsessment: Assessment;
}

export function SymptomAsessmentDetail({
  onClose,
  symptomAsessment,
}: SymptomAsessmentDetailProps) {
  const { t } = useTranslation();
  const { t: symptomT } = useCustomTranslation('symptom');

  const assessmentItem: SymptomAsessmentData =
    symptomAsessment.assessment.assessment;

  const noOfSymptoms = `${assessmentItem.score}/${assessmentItem.totalSteps}`;
  const severityScore = `${assessmentItem.data
    .map((item) => item.answer)
    .reduce((partialSum, a) => partialSum + a, 0)}/${
    assessmentItem.totalSteps * 6
  }`;

  return (
    <Grid container direction="column" sx={{ mb: 4 }}>
      <Paper
        square
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 80,
          width: '100%',
          bgcolor: 'background.default',
        }}
      >
        <div>
          <Typography variant="body1" color="primary">
            {symptomT('VIEW_SYMPTOM_DETAIL_TITLE')}
          </Typography>
          <Typography variant="subtitle2">
            {dateTime.format(
              symptomAsessment.originalCreatedAt ?? symptomAsessment.createdAt,
              t('common.datetime.FORMAT_STANDARD_DATE_TIME'),
            )}
          </Typography>
        </div>
        <IconButton
          aria-label={t('accessibility.CLOSE')}
          sx={{ marginLeft: 'auto' }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </Paper>

      <Divider />

      {assessmentItem.data.map((item: SymptomAsessmentDataDetail) => (
        <Grid
          container
          sx={{ px: 2, mt: 2 }}
          justifyContent="space-between"
          key={item.title.l10nKey}
        >
          <Grid item>
            <Typography variant="subtitle2">
              {symptomT(item.title.l10nKey)}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2"> {item.answer}</Typography>
          </Grid>
        </Grid>
      ))}

      <Divider sx={{ my: 2 }} />

      <Grid container sx={{ px: 2, mt: 2 }} justifyContent="space-between">
        <Grid item>
          <Typography variant="subtitle2">
            {symptomT('VIEW_SYMPTOM_DETAIL_TOTAL_NO')}
          </Typography>
        </Grid>
        <Typography variant="subtitle2">
          <Grid item>{noOfSymptoms}</Grid>
        </Typography>
      </Grid>
      <Grid container sx={{ px: 2, mt: 2 }} justifyContent="space-between">
        <Grid item>
          <Typography variant="subtitle2">
            {symptomT('VIEW_SYMPTOM_DETAIL_SEVERITY_SCORE')}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="subtitle2">{severityScore}</Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}
